import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, database } from '../firebase';
import { ref, get } from 'firebase/database';
import { collection, getDocs, doc, writeBatch, query, where, collectionGroup } from 'firebase/firestore';

function buildMaps(metadata) {
    const nameToCode = {};
    const codeToName = {};
    if (metadata?.cuerpos_y_servicios) {
        Object.values(metadata.cuerpos_y_servicios).forEach(display => {
            const code = display.includes('(') ? display.split('(').pop().replace(')', '').trim() : display;
            const upperCode = code.toUpperCase();
            nameToCode[upperCode] = upperCode;
            nameToCode[display] = upperCode;
            nameToCode[display.toUpperCase()] = upperCode;
            codeToName[upperCode] = display;
        });
    }
    if (metadata?.grados) {
        Object.values(metadata.grados).forEach(display => {
            const upper = display.toUpperCase();
            nameToCode[upper] = upper;
            nameToCode[display] = upper;
            codeToName[upper] = display;
        });
    }
    if (metadata?.especialidades) {
        Object.entries(metadata.especialidades).forEach(([corps, specialties]) => {
            if (Array.isArray(specialties)) {
                specialties.forEach(s => {
                    const code = s.includes('(') ? s.split('(').pop().replace(')', '').trim() : s;
                    const upperCode = code.toUpperCase();
                    nameToCode[upperCode] = upperCode;
                    nameToCode[s] = upperCode;
                    nameToCode[s.toUpperCase()] = upperCode;
                    codeToName[upperCode] = s;
                });
            }
        });
    }
    return { nameToCode, codeToName };
}

function AdminVaultCleaner() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterHierarchy, setFilterHierarchy] = useState('');
    const [filterCorps, setFilterCorps] = useState('');
    const [filterSpecialty, setFilterSpecialty] = useState('');
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [actionLoading, setActionLoading] = useState(null);
    const [metadata, setMetadata] = useState(null);
    const [metadataMaps, setMetadataMaps] = useState({ nameToCode: {}, codeToName: {} });

    useEffect(() => {
        (async () => {
            try {
                const snap = await get(ref(database, 'app_metadata'));
                if (snap.exists()) {
                    const data = snap.val();
                    setMetadata({
                        cuerpos: data.cuerpos_y_servicios ? Object.values(data.cuerpos_y_servicios) : [],
                        grados: data.grados ? Object.values(data.grados) : [],
                        especialidades: data.especialidades || {},
                    });
                    setMetadataMaps(buildMaps(data));
                }
            } catch (e) {
                console.error('Error loading app_metadata:', e);
            }
        })();
    }, []);

    const loadItems = async () => {
        setLoading(true);
        try {
            const results = [];
            const seenIds = new Set();

            const addDocs = (snap) => {
                snap.docs.forEach(doc => {
                    if (!seenIds.has(doc.id)) {
                        seenIds.add(doc.id);
                        results.push({
                            id: doc.id,
                            ref: doc.ref,
                            path: doc.ref.path,
                            ...doc.data(),
                            hierarchyCode: (doc.data().hierarchyCode || '').toUpperCase(),
                            corpsCode: (doc.data().corpsCode || '').toUpperCase(),
                            specialtyCode: (doc.data().specialtyCode || '').toUpperCase(),
                        });
                    }
                });
            };

            const q1 = query(collection(db, 'shared_vault'), where('isVault', '==', true));
            addDocs(await getDocs(q1));

            const q2 = query(collectionGroup(db, 'quizzes'), where('isVault', '==', true));
            addDocs(await getDocs(q2));

            const q3 = query(collection(db, 'shared_vault'), where('tipo', 'in', ['general', 'general_promoted']));
            addDocs(await getDocs(q3));

            results.sort((a, b) => (b.timestamp?.toDate?.() || 0) - (a.timestamp?.toDate?.() || 0));
            setItems(results);
        } catch (err) {
            if (err.code === 'failed-precondition') {
                alert('⚠️ Falta un índice compuesto en Firestore. Abre la consola (F12) y haz clic en el enlace del error para crearlo.');
            } else {
                alert('Error al cargar: ' + err.message);
            }
            console.error(err);
        }
        setLoading(false);
    };

    const getGroupKey = (item) => {
        if (item.standardizedId) return item.standardizedId;
        return `${item.titulo || item.name || 'sin_titulo'}___${item.questionCount || 0}`;
    };

    const grouped = useMemo(() => {
        const map = {};
        items.forEach(item => {
            const key = getGroupKey(item);
            if (!map[key]) map[key] = [];
            map[key].push(item);
        });
        const arr = Object.entries(map).map(([key, groupItems]) => ({
            key,
            items: groupItems,
            count: groupItems.length,
            hasGlobal: groupItems.some(i => i.tipo === 'general' || i.tipo === 'general_promoted'),
            titulo: groupItems[0]?.titulo || groupItems[0]?.name || 'Sin título',
            standardizedId: groupItems[0]?.standardizedId || null,
        }));
        const nc = metadataMaps.nameToCode;
        const filtered = arr.filter(group => {
            if (!filterHierarchy && !filterCorps && !filterSpecialty) return true;
            const hCode = nc[filterHierarchy] || filterHierarchy;
            const cCode = nc[filterCorps] || filterCorps;
            const sCode = nc[filterSpecialty] || filterSpecialty;
            return group.items.some(i => {
                if (filterHierarchy && i.hierarchyCode !== hCode) return false;
                if (filterCorps && i.corpsCode !== cCode) return false;
                if (filterSpecialty && i.specialtyCode !== sCode) return false;
                return true;
            });
        });
        return filtered;
    }, [items, filterHierarchy, filterCorps, filterSpecialty, metadataMaps]);

    const handleKeepAndDelete = async (keepItem, group) => {
        const toDelete = group.items.filter(i => i.id !== keepItem.id);
        if (toDelete.length === 0) return alert('No hay duplicados para eliminar.');
        if (!window.confirm(`¿Mantener "${keepItem.titulo || keepItem.name}" y eliminar ${toDelete.length} duplicado(s)?`)) return;
        setActionLoading(keepItem.id);
        try {
            const batch = writeBatch(db);
            for (const item of toDelete) {
                batch.delete(doc(db, item.path || ('shared_vault/' + item.id)));
            }
            await batch.commit();
            setItems(prev => prev.filter(i => !toDelete.find(d => d.id === i.id)));
            alert(`Eliminados ${toDelete.length} duplicado(s).`);
        } catch (err) {
            alert('Error al eliminar: ' + err.message);
        }
        setActionLoading(null);
    };

    const handleConvertToGlobal = async (item) => {
        if (!window.confirm(`¿Convertir "${item.titulo || item.name}" en Global (tipo: general)?`)) return;
        setActionLoading(item.id);
        try {
            const batch = writeBatch(db);
            batch.update(doc(db, item.path || ('shared_vault/' + item.id)), { tipo: 'general' });
            const group = grouped.find(g => g.items.some(i => i.id === item.id));
            if (group) {
                const others = group.items.filter(i => i.id !== item.id && (i.tipo === 'general' || i.tipo === 'general_promoted'));
                for (const other of others) {
                    batch.update(doc(db, other.path || ('shared_vault/' + other.id)), { tipo: 'por_jerarquia' });
                }
            }
            await batch.commit();
            setItems(prev => prev.map(i => i.id === item.id ? { ...i, tipo: 'general' } : i));
            alert('Convertido a Global exitosamente.');
        } catch (err) {
            alert('Error: ' + err.message);
        }
        setActionLoading(null);
    };

    const toggleGroup = (key) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    const getName = (code, type) => {
        if (!code) return '—';
        const maps = metadataMaps;
        const name = maps.codeToName?.[code];
        return name || code;
    };

    function formatDate(ts) {
        if (!ts) return '—';
        const d = ts.toDate ? ts.toDate() : new Date(ts);
        return d.toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    }

    const hierarchyOptions = metadata?.grados || [];
    const corpsOptions = metadata?.cuerpos || [];
    const specialtyOptions = useMemo(() => {
        if (!filterCorps || !metadata?.especialidades) return [];
        const corpsName = metadataMaps.codeToName?.[filterCorps] || filterCorps;
        const found = Object.entries(metadata.especialidades).find(([k]) =>
            k.toLowerCase().includes(corpsName.toLowerCase()) || corpsName.toLowerCase().includes(k.toLowerCase())
        );
        return found?.[1] || [];
    }, [filterCorps, metadata, metadataMaps]);

    return (
        <div className="admin-quizz-mgmt">
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                <button className="btn-secondary" onClick={() => navigate('/dashboard/admin')}>← Volver</button>
                <h2 style={{ margin: 0 }}>🧹 Limpieza y Consolidación de Bóveda</h2>
            </div>

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '15px', alignItems: 'center' }}>
                <select value={filterHierarchy} onChange={e => setFilterHierarchy(e.target.value)} style={selectStyle}>
                    <option value="">Todas las Jerarquías</option>
                    {hierarchyOptions.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
                <select value={filterCorps} onChange={e => { setFilterCorps(e.target.value); setFilterSpecialty(''); }} style={selectStyle}>
                    <option value="">Todos los Cuerpos</option>
                    {corpsOptions.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={filterSpecialty} onChange={e => setFilterSpecialty(e.target.value)} style={selectStyle}>
                    <option value="">Todas las Especialidades</option>
                    {specialtyOptions.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <button className="btn-primary" onClick={loadItems} disabled={loading}>
                    {loading ? '🔄 Cargando...' : '📥 Cargar Bóveda'}
                </button>
                <span style={{ fontSize: '0.85rem', opacity: 0.6 }}>
                    {grouped.length} grupo(s), {items.length} total items
                </span>
            </div>

            {grouped.length === 0 && !loading && (
                <p style={{ textAlign: 'center', opacity: 0.5, padding: '40px' }}>
                    Presiona "Cargar Bóveda" para comenzar.
                </p>
            )}

            {grouped.map(group => (
                <div key={group.key} className="admin-card-expandable" style={{ marginBottom: '12px' }}>
                    <div
                        className="section-header"
                        onClick={() => toggleGroup(group.key)}
                        style={{ cursor: 'pointer', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span>{expandedGroups.has(group.key) ? '▼' : '▶'}</span>
                            <strong>{group.titulo}</strong>
                            {group.standardizedId && <code style={{ fontSize: '0.7rem', opacity: 0.5 }}>{group.standardizedId}</code>}
                            <span className="chip" style={{ background: group.hasGlobal ? 'var(--success-color)' : '#f44336', color: 'white' }}>
                                {group.hasGlobal ? '🌐 Global' : '🔴 Sin Global'}
                            </span>
                            <span className="chip">{group.count} item(s)</span>
                        </div>
                    </div>

                    {expandedGroups.has(group.key) && (
                        <div className="section-body" style={{ padding: '10px 16px' }}>
                            {group.items.map(item => (
                                <div key={item.id} style={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    padding: '8px 12px', marginBottom: '6px', borderRadius: '8px',
                                    background: item.tipo === 'general' || item.tipo === 'general_promoted'
                                        ? 'rgba(76,175,80,0.15)' : 'rgba(255,255,255,0.03)',
                                    border: item.tipo === 'general' || item.tipo === 'general_promoted'
                                        ? '1px solid rgba(76,175,80,0.3)' : '1px solid rgba(255,255,255,0.05)',
                                }}>
                                    <div style={{ flex: 1, fontSize: '0.8rem' }}>
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                            <code style={{ fontSize: '0.65rem', opacity: 0.6 }}>{item.id.slice(0, 8)}...</code>
                                            {item.tipo === 'general' && <span className="chip" style={{ background: '#4CAF50', color: 'white', fontSize: '0.6rem' }}>GLOBAL</span>}
                                            {item.tipo === 'general_promoted' && <span className="chip" style={{ background: '#FF9800', color: 'white', fontSize: '0.6rem' }}>PROMOCIONADO</span>}
                                            {(item.hierarchyCode || item.corpsCode) && (
                                                <span style={{ opacity: 0.5 }}>
                                                    🎖️ {getName(item.hierarchyCode, 'hierarchy')} / 📂 {getName(item.corpsCode, 'corps')} / 🏷️ {getName(item.specialtyCode, 'specialty')}
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ marginTop: '4px', opacity: 0.6, fontSize: '0.7rem' }}>
                                            👤 {item.authorId ? `${item.authorId.slice(0, 8)}...` : 'Anónimo'} | 📅 {formatDate(item.timestamp || item.createdAt)} | ❓ {item.questionCount || 0} preg.
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0, marginLeft: '10px' }}>
                                        <button
                                            className="btn-mini-approve"
                                            onClick={() => handleKeepAndDelete(item, group)}
                                            disabled={actionLoading === item.id}
                                            style={{ fontSize: '0.7rem', padding: '4px 8px' }}
                                        >
                                            {actionLoading === item.id ? '...' : '✅ Mantener y borrar duplicados'}
                                        </button>
                                        <button
                                            className="btn-mini-action"
                                            onClick={() => handleConvertToGlobal(item)}
                                            disabled={actionLoading === item.id}
                                            style={{ fontSize: '0.7rem', padding: '4px 8px' }}
                                        >
                                            {actionLoading === item.id ? '...' : '🌐 Convertir en Global'}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}

            <style>{`
                .admin-quizz-mgmt { color: white; padding: 10px; max-width: 1200px; margin: 0 auto; }
                .admin-card-expandable { background: var(--bg-secondary); border-radius: 15px; overflow: hidden; border: 1px solid rgba(255,255,255,0.05); transition: all 0.3s ease; }
                .admin-card-expandable:hover { border-color: var(--accent); transform: translateY(-2px); box-shadow: 0 5px 15px rgba(0,0,0,0.3); }
                .section-header { background: rgba(255,255,255,0.02); border-bottom: 1px solid rgba(255,255,255,0.05); }
                .section-body { animation: slideDown 0.3s ease-out; }
                .chip { padding: 3px 10px; border-radius: 20px; font-size: 0.7rem; background: rgba(255,255,255,0.08); }
                .btn-primary { padding: 8px 16px; background: var(--accent); color: white; border: none; border-radius: 8px; cursor: pointer; font-size: 0.85rem; }
                .btn-primary:disabled { opacity: 0.5; }
                .btn-secondary { padding: 8px 16px; background: rgba(255,255,255,0.1); color: white; border: none; border-radius: 8px; cursor: pointer; font-size: 0.85rem; }
                .btn-mini-approve { background: #4CAF50; color: white; border: none; border-radius: 4px; cursor: pointer; white-space: nowrap; }
                .btn-mini-approve:disabled { opacity: 0.5; }
                .btn-mini-action { background: var(--accent); color: white; border: none; border-radius: 4px; cursor: pointer; white-space: nowrap; }
                .btn-mini-action:disabled { opacity: 0.5; }
                @keyframes slideDown {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
}

const selectStyle = {
    padding: '8px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(0,0,0,0.2)', color: 'white', fontSize: '0.8rem',
};

export default AdminVaultCleaner;
